import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import searchIcon from '@/assets/search.svg';
import SearchHistoryBox from './SearchHistoryBox';
import SearchResultBox from './SearchResultBox';
import useDebounce from '@/hooks/useDebounce';
import useSearchHistory from '@/hooks/useSearchHistory';
import { getCards } from '@/api/products';

/**
 * Search input component with dropdown history and results
 */
const SearchInput = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // Search history hook
  const { searchHistory, addToHistory, clearHistory } = useSearchHistory();
  
  // Debounce search input
  const debouncedSearchText = useDebounce(searchText, 1000);
  
  // Query for search results
  const { 
    data: searchResults, 
    isLoading,
    isFetching 
  } = useQuery({
    queryKey: ['searchCards', debouncedSearchText],
    queryFn: () => getCards({
      sort: 'POPULARITY',
      direction: 'DESC',
      keyword: debouncedSearchText
    }),
    enabled: debouncedSearchText.length > 0,
    select: data => data.data?.slice(0, 10),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Determine if we should show "has more" button
  const hasMoreResults = searchResults && searchResults.length >= 10;

  // Handle search input changes
  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    if (e.target.value.length > 0) {
      setShowResults(true);
    } else {
      setShowResults(false);
    }
  };

  // Handle clearing search text
  const handleClearSearch = () => {
    setSearchText('');
    setShowResults(false);
    searchInputRef.current?.focus();
  };

  // Handle search form submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchText.trim()) {
      addToHistory(searchText);
      navigate(`/search?q=${encodeURIComponent(searchText)}`);
      setIsFocused(false);
      setShowResults(false);
      // Explicitly blur the input element
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
    }
  };

  // Handle result selection
  const handleSelectResult = (item) => {
    addToHistory(searchText);
    // Navigate to the detail page
    navigate(`/card/${item.id}`);
    setIsFocused(false);
  };

  // Handle history item selection
  const handleSelectHistory = (term) => {
    setSearchText(term);
    navigate(`/search?q=${encodeURIComponent(term)}`);
    setIsFocused(false);
    setShowResults(false);
  };

  // Handle view all results click
  const handleViewAllResults = () => {
    if (searchText.trim()) {
      addToHistory(searchText);
      navigate(`/search?q=${encodeURIComponent(searchText)}`);
      setIsFocused(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Determine if we should show the search history box
  const shouldShowHistory = isFocused && !showResults && searchHistory.length > 0;
  
  // Determine if we should show the search results box
  const shouldShowResults = isFocused && showResults && searchResults && searchResults.length > 0;

  return (
    <div className="relative">
      <form onSubmit={handleSearchSubmit}>
        <div 
          className="w-[368px] h-9 px-3 bg-[#f4f4f4] rounded-[47px] border border-[#dddddd] flex items-center gap-1.5"
        >
          <img src={searchIcon} alt="Search" className="w-4 h-4" />
          <input 
            type="text" 
            value={searchText}
            onChange={handleSearchChange}
            onFocus={() => setIsFocused(true)}
            placeholder={t('common.search_placeholder')}
            className="bg-transparent text-sm text-[#111111] placeholder:text-[#bbbbbb] outline-none w-full"
            ref={searchInputRef}
          />
          {searchText && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="w-4 h-4 flex items-center justify-center text-[#bbbbbb] hover:text-[#888888]"
              aria-label="Clear search"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
      </form>

      {/* Show dropdown based on focus state and input text */}
      {(shouldShowHistory || shouldShowResults) && (
        <div 
          ref={dropdownRef}
          className="absolute top-[44px] left-0 z-50 w-[368px]"
        >
          {shouldShowResults ? (
            <SearchResultBox 
              results={searchResults || []}
              hasMore={hasMoreResults}
              onViewAllResults={handleViewAllResults}
              onSelectResult={handleSelectResult}
              searchQuery={searchText}
              className="w-full"
            />
          ) : shouldShowHistory && (
            <SearchHistoryBox 
              searchHistory={searchHistory}
              onClearHistory={clearHistory}
              onSelectHistory={handleSelectHistory}
              className="w-full"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SearchInput; 